import {
  DatePicker as MUIDatePicker,
  type DatePickerProps as MUIDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';

import { FormHelperText, type TextFieldProps } from '@/primitives';
import { STANDARD_DATE_FORMAT } from '@/configs';
import {
  compactFilledFloatingLabelSx,
  compactFilledFieldHelperTextSx,
  compactFilledInputValueSx,
  defaultFilledFloatingLabelSx,
  defaultFilledInputValueSx,
} from '@/lib/compactFilledFieldStyles';

// MUI X v8 DatePickerProps generic is boolean (EnableAccessibleFieldDOMStructure), not date type
export interface DatePickerProps extends MUIDatePickerProps {
  dateFormat?: string;
  error?: TextFieldProps['error'];
  /** Smaller label/input (entity form DateField). */
  fieldDensity?: 'default' | 'compact';
  helperText?: TextFieldProps['helperText'];
  required?: TextFieldProps['required'];
  variant?: TextFieldProps['variant'];
}

export function DatePicker({
  dateFormat = STANDARD_DATE_FORMAT,
  error,
  fieldDensity = 'default',
  helperText,
  ...props
}: DatePickerProps) {
  const textFieldSlot =
    props.slotProps?.textField && typeof props.slotProps.textField === 'object'
      ? props.slotProps.textField
      : undefined;
  const inputPropsFromSlot = textFieldSlot?.InputProps;
  const inputPropsSxFromSlot =
    inputPropsFromSlot &&
    typeof inputPropsFromSlot === 'object' &&
    'sx' in inputPropsFromSlot
      ? inputPropsFromSlot.sx
      : undefined;

  const compact = fieldDensity === 'compact';

  if (props.variant === 'filled') {
    return (
      <>
        <MUIDatePicker
          format={dateFormat}
          {...props}
          slotProps={{
            ...props.slotProps,
            textField: {
              ...textFieldSlot,
              error,
              required: props.required,
              size: compact ? 'small' : textFieldSlot?.size,
              variant: 'filled',
              InputLabelProps: {
                ...textFieldSlot?.InputLabelProps,
                shrink: true,
                sx: {
                  ...(compact
                    ? compactFilledFloatingLabelSx
                    : defaultFilledFloatingLabelSx),
                  ...(props.disabled ? { color: 'text.disabled' } : {}),
                  ...(textFieldSlot?.InputLabelProps &&
                  'sx' in textFieldSlot.InputLabelProps &&
                  textFieldSlot.InputLabelProps.sx &&
                  typeof textFieldSlot.InputLabelProps.sx === 'object' &&
                  !Array.isArray(textFieldSlot.InputLabelProps.sx)
                    ? textFieldSlot.InputLabelProps.sx
                    : {}),
                },
              },
              InputProps: {
                disableUnderline: true,
                sx: {
                  ...(compact
                    ? compactFilledInputValueSx
                    : defaultFilledInputValueSx),
                  bgcolor: 'background.paper',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                  ':focus-within': {
                    borderColor: 'primary.main',
                    bgcolor: 'background.paper',
                  },
                  ':has(input[aria-invalid="true"])': {
                    borderColor: 'error.main',
                  },
                  '&.MuiFilledInput-root': {
                    backgroundColor: 'transparent',
                  },
                  '&.MuiFilledInput-root:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:not(.Mui-disabled):hover': {
                    bgcolor: 'background.paper',
                  },
                  '&.MuiFilledInput-root.Mui-focused': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-disabled': {
                    borderRadius: '8px',
                    fontStyle: 'italic',
                    bgcolor: 'action.disabledBackground',
                    color: 'text.disabled',
                    cursor: 'default',
                    '& input, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-section':
                      {
                        color: 'text.disabled',
                        cursor: 'default',
                      },
                    '& .MuiInputAdornment-root': {
                      visibility: 'hidden',
                    },
                  },
                  '&.Mui-disabled:hover': {
                    bgcolor: 'action.disabledBackground',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: compact ? '0.8125rem' : '14px',
                    lineHeight: compact ? '18px' : '20px',
                  },
                  ...inputPropsSxFromSlot,
                  // MUI X field often does not put `MuiInputBase-sizeSmall` on the same root as the
                  // picker adornment; theme defaults would bump compact dates to 1.25rem — pin here.
                  '& .MuiInputAdornment-positionEnd': {
                    paddingRight: compact ? '6px' : '8px',
                  },
                  '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: compact ? '4px' : '6px',
                  },
                  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                    fontSize: compact ? '1.125rem' : '1.25rem',
                  },
                },
              },
            },
          }}
        />
        {error && helperText ? (
          <FormHelperText
            error={error}
            sx={{
              marginLeft: compact ? '12px' : '14px',
              ...(compact ? compactFilledFieldHelperTextSx : {}),
            }}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </>
    );
  }

  return (
    <>
      <MUIDatePicker
        format={dateFormat}
        {...props}
        slotProps={{
          textField: {
            required: props.required,
            variant: props.variant,
          },
        }}
      />
      {error && helperText ? (
        <FormHelperText error={error} sx={{ marginLeft: '14px' }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </>
  );
}
